import {useContext, useMemo} from 'react'
import _ from 'lodash'
import {DataSourcesContext, IDataSourceItem} from '@/features/data-sources'
import {IMixerChannel, IMixerChannelCollectionProps} from '..'

export interface ISortScores {
  scores: {[key: string]: number}
  mean: number
}

export interface ICutStates {
  inRange: {[key: string]: boolean}
  shouldCut: boolean
}

export interface IUseChannelComponentsResponse<T extends IDataSourceItem> {
  sort: {
    descriptor: IMixerChannel['descriptor']
    value: IMixerChannel['value']['sort']
    range: IMixerChannel['descriptor']['range']
  }[]
  cut: {
    descriptor: IMixerChannel['descriptor']
    value: IMixerChannel['value']['cut']
    range: IMixerChannel['descriptor']['range']
  }[]
  applyPatches: (changes: IMixerChannel[]) => IMixerChannel[]
  calculateSortScores: (media: T) => ISortScores
  calculateCuts: (media: T) => ICutStates
}

export function useChannelComponents<T extends IDataSourceItem>(
  channels?: IMixerChannelCollectionProps['channels']
): IUseChannelComponentsResponse<T> {
  const {mergedItemsStats} = useContext(DataSourcesContext)

  const state: IUseChannelComponentsResponse<T> = useMemo(() => {
    const soloChannels = _.chain(channels || [])
      .filter((c) => c.value.solo || false)
      .map((c) => c.descriptor.sourcePath)
      .value()
    const [sort, cut] = _.chain(channels || [])
      .map((channel) => {
        const {descriptor, value} = channel
        const {sourcePath, range} = descriptor
        if (soloChannels.length > 0) {
          if (!_.includes(soloChannels, sourcePath)) return [undefined, undefined]
        }
        const {isInitialized, sort, cut, mute} = value
        if (!isInitialized) return [undefined, undefined]
        if (mute) return [undefined, undefined]
        return [
          sort ? {descriptor, value: sort, range} : undefined,
          cut ? {descriptor, value: cut, range} : undefined,
        ]
      })
      .unzip()
      .map(_.compact)
      .value() as [
      IUseChannelComponentsResponse<T>['sort'],
      IUseChannelComponentsResponse<T>['cut'],
    ]
    return {
      sort,
      cut,
      applyPatches,
      calculateSortScores,
      calculateCuts,
    }
  }, [channels])

  function applyPatches(changes: IMixerChannel[]): IMixerChannel[] {
    const res: IMixerChannel[] = _.chain(channels)
      .concat(changes)
      .groupBy((c) => c.descriptor.sourcePath)
      .toPairs()
      .map(([_k, v]) => _.last(v))
      .compact()
      .value()
    return res
  }

  function calculateSortScores(media: T): ISortScores {
    const {sort} = state
    const scores: ISortScores['scores'] = _.chain(sort)
      .map(({descriptor, value, range}) => {
        const {kind, word, sourcePath} = descriptor
        if (!sourcePath) return
        const sourceValueType = mergedItemsStats.fieldTypes[kind]
        const sourceValue =
          sourceValueType === 'string_array' ?
            _.includes(_.get(media, kind), word) ?
              1
            : 0
          : _.get(media, sourcePath, 0)
        return [
          sourcePath,
          ((sourceValue - range.min) / ((range.max || 1) - (range.min || 0))) * value,
        ]
      })
      .compact()
      .fromPairs()
      .value()
    return {
      scores,
      mean: _.mean(_.values(scores)),
    }
  }

  function calculateCuts(media: T): ICutStates {
    const {cut} = state
    const inRange: ICutStates['inRange'] = _.chain(cut)
      .map(({descriptor, value}) => {
        const {sourcePath, kind, word} = descriptor
        if (!sourcePath) return
        let res = true
        if (value) {
          const sourceValueType = mergedItemsStats.fieldTypes[kind]
          const sourceValue =
            sourceValueType === 'string_array' ?
              _.includes(_.get(media, kind), word) ?
                1
              : 0
            : _.get(media, sourcePath, 0)
          if (sourceValue < value.min || sourceValue > value.max) res = false
        }
        return [sourcePath, res]
      })
      .fromPairs()
      .value()
    return {
      inRange,
      shouldCut: _.includes(_.values(inRange), false),
    }
  }

  return state
}
